import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { RequireAccess } from './auth/RequireAccess';
import { can, canPlan } from './auth/me';
import { ChangePasswordPage } from './auth/ChangePasswordPage';
import { MyAccountPage } from './auth/MyAccountPage';
import { LoginPage } from './auth/LoginPage';
import { useMe } from './auth/useMe';
import { RolePermissionsPage } from './admin/RolePermissionsPage';
import { UserOverridesPage } from './admin/UserOverridesPage';
import { UsersPage } from './admin/UsersPage';
import { CatalogPage } from './catalog/CatalogPage';
import { CategoryOrderPage } from './catalog/CategoryOrderPage';
import { ContractorsPage } from './contractors/ContractorsPage';
import { HintsEditorPage } from './hints/HintsEditorPage';
import { ManualPage } from './hints/ManualPage';
import { AvailabilityPage } from './availability/AvailabilityPage';
import { EventsPage } from './events/EventsPage';
import { CheckoffPage } from './packlists/CheckoffPage';
import { PacklistEditorPage } from './packlists/PacklistEditorPage';
import { KitsPage } from './kits/KitsPage';
import { FunctionsPage } from './people/FunctionsPage';
import { PeoplePage } from './people/PeoplePage';
import { BlocksPage } from './templates/BlocksPage';
import { TemplatesPage } from './templates/TemplatesPage';
import { RulesPage } from './rules/RulesPage';
import { FailedPage } from './notifications/FailedPage';
import { InboxPage } from './notifications/InboxPage';
import { MyCalendarPage } from './crew/MyCalendarPage';
import { TodoPage } from './todo/TodoPage';
import { SummaryPage } from './todo/SummaryPage';
import { DaySchemesPage } from './pricing/DaySchemesPage';
import { canSeePricing, PACKAGES_RESOURCE, PricingIndex, QUOTE_BLOCKS_RESOURCE, QUOTE_SETTINGS_RESOURCE, SCHEMES_RESOURCE, SERVICES_RESOURCE, SUPPLIER_PRICES_RESOURCE } from './pricing/PricingLayout';
import { PackagesPage } from './pricing/PackagesPage';
import { QuoteBlocksPage } from './pricing/QuoteBlocksPage';
import { SupplierPricesPage } from './subrentals/SupplierPricesPage';
import { SupplierCatalogPage, SupplierListPage } from './subrentals/SupplierCatalogPage';
import { SuppliersTab } from './finance/SuppliersTab';
import { NewInPanelRedirect } from './events/NewInPanelRedirect';
import { TransportPage } from './pricing/TransportPage';
import { ServicesPage } from './pricing/ServicesPage';
import { QuoteSettingsPage } from './quotes/QuoteSettingsPage';
import { QuotesPage } from './quotes/QuotesPage';
import { OfferPage } from './quotes/offers/OfferPage';
import { AppLayout } from './layout/AppLayout';
import { HomePage } from './layout/HomePage';
import { SettingsPage } from './settings/SettingsPage';
import { TasksPage } from './company-tasks/TasksPage';
import { FinancePage } from './finance/FinancePage';
import { SettlementsPage } from './settlements/SettlementsPage';
import { canSeeFinanceTab, FinanceIndex, FinanceLayout, LegacyPricingRedirect } from './finance/FinanceLayout';
import { canSeeFinance } from './finance/financeTabs';
import { StatementsPage } from './bank/StatementsPage';
import { ContractsPage } from './contracts/ContractsPage';
import { TemplatesPage as ContractTemplatesPage } from './contracts/TemplatesPage';
import { BarterPage } from './barter/BarterPage';
import { ReceivablesPage } from './payments/ReceivablesPage';
import { InvestmentsPage } from './purchases/InvestmentsPage';
import { ItemsPage } from './finance/ItemsPage';
import { RentalsReportPage } from './finance/RentalsReportPage';
import { PurchaseDetailPage, PurchasesPage } from './purchases/PurchasesPage';
import { InvoiceCard } from './invoices/InvoiceCard';
import { InvoicesPage } from './invoices/InvoicesPage';
import { AnswerPage } from './requests/AnswerPage';
import { QuotePrintPage } from './quotes/print/QuotePrintPage';

function Protected() {
  const { me, isLoading } = useMe();
  if (isLoading) return <div className="boot" role="status">Wczytywanie…</div>;
  if (!me) return <Navigate to="/login" replace />;
  return <AppLayout me={me} />;
}

/** A page of its own, without the menu: the print view of a quote (wzór F) is printed as it is. */
function ProtectedBare() {
  const { me, isLoading } = useMe();
  if (isLoading) return <div className="boot" role="status">Wczytywanie…</div>;
  if (!me) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* the answer to an availability request: no login (a technician without an account, from the SMS) */}
      <Route path="/odpowiedz/:token" element={<AnswerPage />} />
      <Route element={<ProtectedBare />}>
        <Route element={<RequireAccess check={(me) => can(me, 'quotes.quotes', 'view')} />}>
          <Route path="wydruk/kosztorys/:versionId" element={<QuotePrintPage />} />
        </Route>
      </Route>
      <Route element={<Protected />}>
        <Route index element={<HomePage />} />
        <Route path="haslo" element={<ChangePasswordPage />} />
        <Route path="moje-konto" element={<MyAccountPage />} />
        <Route path="instrukcja" element={<ManualPage />} />
        <Route path="powiadomienia" element={<InboxPage />} />
        <Route path="moj-kalendarz" element={<MyCalendarPage />} />
        {/* F46: filtered by the rights of each entry, so a session is enough */}
        <Route path="do-zrobienia" element={<TodoPage />} />
        {/* "Podsumowanie" (owner 2026-10-24): the same rule, every section checks its entries */}
        <Route path="podsumowanie" element={<SummaryPage />} />
        <Route element={<RequireAccess resource="notifications.failed" action="view" />}>
          <Route path="powiadomienia/wysylka" element={<FailedPage />} />
        </Route>
        <Route element={<RequireAccess resource="hints.content" action="view" />}>
          <Route path="tresc-podpowiedzi" element={<HintsEditorPage />} />
        </Route>
        <Route element={<RequireAccess resource="identity.users" action="view" />}>
          <Route path="uzytkownicy" element={<UsersPage />} />
        </Route>
        <Route element={<RequireAccess resource="contractors.contractors" action="view" />}>
          <Route path="kontrahenci" element={<ContractorsPage />} />
        </Route>
        {/* each section of the settings page checks its own right (the server too) */}
        <Route path="ustawienia" element={<SettingsPage />} />
        {/* people or functions: the page itself sends a role that sees only functions to them */}
        <Route path="osoby" element={<PeoplePage />} />
        <Route element={<RequireAccess resource="people.functions" action="view" />}>
          <Route path="osoby/funkcje" element={<FunctionsPage />} />
        </Route>
        <Route element={<RequireAccess resource="catalog.items" action="view" />}>
          <Route path="katalog" element={<CatalogPage />} />
        </Route>
        <Route element={<RequireAccess resource={SUPPLIER_PRICES_RESOURCE} action="view" />}>
          <Route path="katalog/dostawcy" element={<SupplierListPage />} />
          <Route path="katalog/dostawcy/:id" element={<SupplierCatalogPage />} />
        </Route>
        <Route element={<RequireAccess resource="catalog.categories" action="view" />}>
          <Route path="katalog/kolejnosc-kategorii" element={<CategoryOrderPage />} />
        </Route>
        {/* owner 20.10 23:37: kits are a tab of "Szablony" (the old address leads there) */}
        <Route element={<RequireAccess resource="kits.kits" action="view" />}>
          <Route path="szablony/zestawy" element={<KitsPage />} />
        </Route>
        <Route path="zestawy" element={<Navigate to="/szablony/zestawy" replace />} />
        {/* templates, blocks or kits: the page itself sends a role without templates to the blocks or the kits */}
        <Route path="szablony" element={<TemplatesPage />} />
        <Route element={<RequireAccess resource="templates.blocks" action="view" />}>
          <Route path="szablony/bloki" element={<BlocksPage />} />
        </Route>
        <Route element={<RequireAccess resource="rules.rules" action="view" />}>
          <Route path="reguly" element={<RulesPage />} />
        </Route>
        <Route element={<RequireAccess check={(me) => can(me, 'quotes.quotes', 'view')} />}>
          <Route path="wyceny" element={<QuotesPage />} />
          <Route path="wyceny/oferty/:id" element={<OfferPage />} />
        </Route>
        <Route path="kosztorysy" element={<Navigate to="/wyceny" replace />} />
        {/* F32: "Finanse" with tabs; "Cenniki" is one of them (old /cenniki/… links redirect) */}
        <Route path="cenniki/*" element={<LegacyPricingRedirect />} />
        <Route element={<RequireAccess check={canSeeFinance} />}>
          <Route path="finanse" element={<FinanceLayout />}>
            <Route index element={<FinanceIndex />} />
            <Route element={<RequireAccess check={canSeeFinanceTab('podsumowanie')} />}>
              <Route path="podsumowanie" element={<FinancePage segment="zestawienie" />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('rozliczenia')} />}>
              <Route path="rozliczenia" element={<SettlementsPage />} />
              <Route path="rozliczenia/:id" element={<SettlementsPage />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('koszty-stale')} />}>
              <Route path="koszty-stale" element={<FinancePage segment="koszty-stale" />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('ustawienia')} />}>
              <Route path="ustawienia" element={<FinancePage segment="ustawienia" />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('faktury')} />}>
              <Route path="faktury" element={<InvoicesPage />} />
              <Route path="faktury/:id" element={<InvoiceCard />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('wyciagi')} />}>
              <Route path="wyciagi" element={<StatementsPage />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('umowy')} />}>
              <Route path="umowy" element={<ContractsPage />} />
              <Route path="umowy/:id" element={<ContractsPage />} />
            </Route>
            <Route element={<RequireAccess resource="contracts.templates" action="view" />}>
              <Route path="umowy/szablony" element={<ContractTemplatesPage />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('platnosci')} />}>
              <Route path="platnosci" element={<ReceivablesPage />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('barter')} />}>
              <Route path="barter" element={<BarterPage />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('urzadzenia')} />}>
              <Route path="urzadzenia" element={<ItemsPage />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('dopozyczenia')} />}>
              <Route path="dopozyczenia" element={<RentalsReportPage />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('dostawcy')} />}>
              <Route path="dostawcy" element={<SuppliersTab />} />
            </Route>
            <Route element={<RequireAccess check={canSeeFinanceTab('zakupy')} />}>
              <Route path="zakupy" element={<PurchasesPage />} />
            </Route>
            <Route element={<RequireAccess resource="purchases.investments" action="view" />}>
              <Route path="zakupy/inwestycje" element={<InvestmentsPage />} />
            </Route>
            <Route element={<RequireAccess resource="purchases.purchases" action="view" />}>
              <Route path="zakupy/:id" element={<PurchaseDetailPage />} />
            </Route>
            <Route element={<RequireAccess check={canSeePricing} />}>
              <Route path="cenniki" element={<PricingIndex />} />
            </Route>
            <Route element={<RequireAccess resource={SCHEMES_RESOURCE} action="view" />}>
              <Route path="cenniki/schematy-dni" element={<DaySchemesPage />} />
            </Route>
            <Route element={<RequireAccess resource={SERVICES_RESOURCE} action="view" />}>
              <Route path="cenniki/uslugi" element={<ServicesPage />} />
              <Route path="cenniki/transport" element={<TransportPage />} />
            </Route>
            <Route element={<RequireAccess resource={PACKAGES_RESOURCE} action="view" />}>
              <Route path="cenniki/pakiety" element={<PackagesPage />} />
            </Route>
            <Route element={<RequireAccess resource={QUOTE_BLOCKS_RESOURCE} action="view" />}>
              <Route path="cenniki/bloki-kosztorysow" element={<QuoteBlocksPage />} />
            </Route>
            <Route element={<RequireAccess resource={SUPPLIER_PRICES_RESOURCE} action="view" />}>
              <Route path="cenniki/katalogi-dostawcow" element={<SupplierPricesPage />} />
            </Route>
            <Route element={<RequireAccess resource={QUOTE_SETTINGS_RESOURCE} action="view" />}>
              <Route path="cenniki/ustawienia-kosztorysu" element={<QuoteSettingsPage />} />
            </Route>
          </Route>
        </Route>
        <Route element={<RequireAccess resource="availability.reservations" action="view" />}>
          <Route path="dostepnosc" element={<AvailabilityPage />} />
        </Route>
        <Route element={<RequireAccess resource="events.events" action="view" />}>
          <Route path="wydarzenia" element={<EventsPage />} />
        </Route>
        {/* the person responsible sees the tasks given to them without any grant (the server limits the list) */}
        <Route path="zadania" element={<TasksPage />} />
        <Route element={<RequireAccess check={canPlan} />}>
          {/* owner 2026-10-21 23:31: made in the side panel of "Wydarzenia"; the old addresses lead there */}
          <Route path="nowa-impreza" element={<NewInPanelRedirect what="event" />} />
        </Route>
        <Route element={<RequireAccess resource="events.events" action="create" />}>
          <Route path="nowe-wypozyczenie" element={<NewInPanelRedirect what="rental" />} />
        </Route>
        <Route element={<RequireAccess resource="packlists.lists" action="view" />}>
          <Route path="wydarzenia/:eventId/zaladunek" element={<CheckoffPage mode="load" />} />
          <Route path="wydarzenia/:eventId/zwrot" element={<CheckoffPage mode="return" />} />
          <Route path="wydarzenia/:eventId/sprzetowka" element={<PacklistEditorPage />} />
        </Route>
        <Route element={<RequireAccess owner />}>
          <Route path="uzytkownicy/:id/uprawnienia" element={<UserOverridesPage />} />
          <Route path="uprawnienia" element={<RolePermissionsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
